import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home as HomeIcon,
  Users as UsersIcon,
  SlidersHorizontal,
  ClipboardList,
  MoreHorizontal,
  Building2,
  UserCog,
  KeyRound,
  DollarSign,
  Mail,
  LogOut,
  X,
} from 'lucide-react';
import { useAuth } from './AuthContext';
import { authFetch, API } from './api';

const Navigation = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showMore, setShowMore] = useState(false);
  // Nothing emails the operator, so the count has to be visible on arrival.
  const [alerts, setAlerts] = useState(0);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/players', label: 'Players' },
    { path: '/criteria', label: 'Criteria' },
    { path: '/recruit-board', label: 'Recruit Board' },
  ];

  const isSchoolAdmin = user?.role === 'super_admin' || user?.role === 'school_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  if (isSchoolAdmin) {
    navItems.push({ path: '/users', label: 'Users' });
  }

  if (isSuperAdmin) {
    navItems.push({ path: '/schools', label: 'Schools' });
    navItems.push({ path: '/billing', label: 'Billing' });
    navItems.push({ path: '/recruiting-center', label: 'Recruiting' });
  }

  // The bottom bar holds four primary destinations; everything else lives
  // behind "More", which is how a native app handles >5 tabs.
  const tabItems = [
    { path: '/', label: 'Home', Icon: HomeIcon },
    { path: '/players', label: 'Players', Icon: UsersIcon },
    { path: '/criteria', label: 'Criteria', Icon: SlidersHorizontal },
    { path: '/recruit-board', label: 'Board', Icon: ClipboardList },
  ];

  useEffect(() => {
    if (user?.role !== 'super_admin') return;
    let stop = false;
    const load = async () => {
      try {
        const r = await authFetch(`${API}/api/listings/alerts`);
        if (r.ok && !stop) setAlerts((await r.json()).total || 0);
      } catch { /* badge is advisory */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { stop = true; clearInterval(t); };
  }, [user?.role, location.pathname]);

  const isActive = (path) => location.pathname === path;

  // A tab stays lit while you are inside its section (e.g. a player's Fit page)
  const isTabActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const moreIsActive = ['/users', '/schools', '/change-password', '/recruiting-center'].some((p) =>
    location.pathname.startsWith(p)
  );

  // Close transient UI on navigation, and lock the page behind an open sheet
  useEffect(() => {
    setShowMore(false);
    setShowMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = showMore ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMore]);

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-purple-600 text-purple-100',
      school_admin: 'bg-blue-600 text-blue-100',
      coach: 'bg-green-600 text-green-100',
      parent: 'bg-yellow-600 text-yellow-100',
      demo: 'bg-orange-600 text-orange-100',
    };
    const labels = {
      super_admin: 'Super Admin',
      school_admin: 'School Admin',
      coach: 'Coach',
      parent: 'Parent',
      demo: 'Demo',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[role] || 'bg-gray-600 text-gray-100'}`}>
        {labels[role] || role}
      </span>
    );
  };

  const initials = (user?.full_name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  const isDemo = user?.role === 'demo';
  const isTrial = user?.school_name?.includes('Trial Program');
  const trialDaysLeft = (() => {
    if (!user?.expires_at) return null;
    const diff = new Date(user.expires_at) - new Date();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  })();

  const moreLinks = [
    ...(isSchoolAdmin ? [{ path: '/users', label: 'Users', Icon: UserCog }] : []),
    ...(isSuperAdmin ? [{ path: '/schools', label: 'Schools', Icon: Building2 }] : []),
    ...(isSuperAdmin ? [{ path: '/billing', label: 'Billing', Icon: DollarSign }] : []),
    ...(isSuperAdmin ? [{ path: '/recruiting-center', label: 'Recruiting Center', Icon: Mail }] : []),
    { path: '/change-password', label: 'Change Password', Icon: KeyRound },
  ];

  return (
    <React.Fragment>
      {isDemo && (
        <div className="bg-orange-600 text-white text-center px-3 py-1.5 text-xs md:text-sm font-medium">
          DEMO MODE — View only. Contact AiDA Advisory for full access.
        </div>
      )}
      {isTrial && (
        <div className="bg-blue-600 text-white text-center px-3 py-1.5 text-xs md:text-sm font-medium">
          FREE TRIAL — {trialDaysLeft !== null ? (trialDaysLeft > 0 ? `${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""} remaining.` : "Your trial has expired.") : ""}{' '}
          <a href="https://aida-advisory.com/pricing.html" style={{color:'#00E676',fontWeight:700,textDecoration:'underline'}} className="block md:inline md:ml-2">Subscribe to keep your data →</a>
        </div>
      )}

      {/* ---------------- Mobile app bar ---------------- */}
      <header className="app-bar app-chrome md:hidden">
        <div className="flex items-center justify-between h-14 px-4">
          <Link to="/" className="flex items-center gap-2 min-w-0 tap">
            <img src="/SchemeSync_Logo.png" alt="" className="h-8 w-auto flex-shrink-0" />
            <div className="text-lg truncate">
              <span className="font-bold text-white">Scheme</span>
              <span className="font-bold text-[#00D4FF]">Sync</span>
              <span className="font-bold text-[#00E676] ml-1 text-sm">HS</span>
            </div>
          </Link>
          <button
            onClick={() => setShowMore(true)}
            aria-label="Account and settings"
            className="flex items-center justify-center h-9 w-9 rounded-full bg-[#243B55] text-[#00D4FF] text-xs font-bold flex-shrink-0 border border-[#546E7A]/40"
          >
            {initials}
          </button>
        </div>
      </header>

      {/* ---------------- Desktop nav (unchanged layout) ---------------- */}
      <nav className="hidden md:block bg-[#1A2744] border-b border-[#546E7A]/30">
        <div className="max-w-7xl mx-auto px-3 lg:px-6">
          <div className="flex justify-between items-center gap-2 h-16">
            <Link to="/" className="flex items-center gap-3 flex-shrink-0 hover:opacity-80 transition-opacity">
              <img src="/SchemeSync_Logo.png" alt="SchemeSync HS" className="h-10 w-auto" />
              <div className="text-xl hidden xl:block">
                <span className="font-bold text-white">Scheme</span>
                <span className="font-bold text-[#00D4FF]">Sync</span>
                <span className="font-bold text-[#00E676] ml-1.5 text-base">HS</span>
              </div>
            </Link>
            <div className="flex gap-0 lg:gap-1 2xl:gap-6 min-w-0">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-1.5 lg:px-3 py-2 rounded-md text-[13px] lg:text-sm font-medium whitespace-nowrap transition-colors ${isActive(item.path) ? 'text-[#00D4FF] bg-[#243B55]' : 'text-[#B0BEC5] hover:text-white hover:bg-[#243B55]'}`}
                >
                  {item.label}
                  {item.path === '/billing' && alerts > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px]
                                     rounded-full bg-[#EF5350] text-white text-[10px] font-bold px-1">
                      {alerts}
                    </span>
                  )}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-3 relative flex-shrink-0">
              <div
                className="text-right cursor-pointer"
                onClick={() => setShowMenu(!showMenu)}
              >
                <div className="text-sm text-white font-medium">{user?.full_name}</div>
                <div className="flex items-center gap-2 justify-end">
                  {user?.school_name && (
                    <span className="text-xs text-gray-400">{user.school_name}</span>
                  )}
                  {getRoleBadge(user?.role)}
                </div>
              </div>

              {showMenu && (
                <div className="absolute right-0 top-14 bg-[#1A2744] border border-[#546E7A]/50 rounded-lg shadow-xl z-50 w-48 overflow-hidden">
                  <Link
                    to="/change-password"
                    onClick={() => setShowMenu(false)}
                    className="block px-4 py-2.5 text-sm text-gray-300 hover:bg-[#243B55] hover:text-white transition-colors"
                  >
                    Change Password
                  </Link>
                  <button
                    onClick={() => { setShowMenu(false); logout(); }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-600/10 transition-colors border-t border-[#546E7A]/30"
                  >
                    Logout
                  </button>
                </div>
              )}

              {!showMenu && (
                <button
                  onClick={logout}
                  className="hidden lg:block ml-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-red-600/20 rounded-md transition-colors"
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ---------------- Mobile bottom tab bar ---------------- */}
      <nav className="tab-bar app-chrome md:hidden" aria-label="Primary">
        {tabItems.map(({ path, label, Icon }) => (
          <Link
            key={path}
            to={path}
            className={`tab-bar-item ${isTabActive(path) ? 'is-active' : ''}`}
            aria-current={isTabActive(path) ? 'page' : undefined}
          >
            <Icon size={21} strokeWidth={isTabActive(path) ? 2.4 : 1.9} />
            <span>{label}</span>
          </Link>
        ))}
        <button
          onClick={() => setShowMore(true)}
          className={`tab-bar-item ${moreIsActive || showMore ? 'is-active' : ''}`}
          aria-label="More"
        >
          <MoreHorizontal size={21} strokeWidth={moreIsActive || showMore ? 2.4 : 1.9} />
          <span>More</span>
        </button>
      </nav>

      {/* ---------------- "More" sheet ---------------- */}
      {showMore && (
        <div
          className="sheet-overlay md:hidden fixed inset-0 bg-black/60 z-[60] flex items-end"
          onClick={() => setShowMore(false)}
        >
          <div
            className="sheet bg-[#1A2744] w-full rounded-t-2xl border-t border-[#546E7A]/40 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-5 pt-2 pb-4">
              <div className="min-w-0">
                <div className="text-white font-semibold truncate">{user?.full_name}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {user?.school_name && (
                    <span className="text-xs text-gray-400 truncate">{user.school_name}</span>
                  )}
                  {getRoleBadge(user?.role)}
                </div>
              </div>
              <button
                onClick={() => setShowMore(false)}
                aria-label="Close"
                className="text-gray-400 p-1 -mr-1 flex-shrink-0"
              >
                <X size={22} />
              </button>
            </div>

            <div className="border-t border-[#546E7A]/25">
              {moreLinks.map(({ path, label, Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className="flex items-center gap-3 px-5 py-4 text-[15px] text-gray-200 border-b border-[#546E7A]/15 active:bg-[#243B55]"
                >
                  <Icon size={19} className="text-[#00D4FF] flex-shrink-0" />
                  {label}
                  {path === '/billing' && alerts > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5
                                     rounded-full bg-[#EF5350] text-white text-[11px] font-bold px-1.5">
                      {alerts}
                    </span>
                  )}
                </Link>
              ))}
              <button
                onClick={logout}
                className="flex items-center gap-3 px-5 py-4 text-[15px] text-red-400 w-full text-left active:bg-red-600/10"
              >
                <LogOut size={19} className="flex-shrink-0" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default Navigation;
